import React, { Fragment } from 'react';
import useAppContext from 'flow/hooks/useAppContext';
import {
    Listbox,
    Transition,
    ListboxButton,
    ListboxOptions,
    ListboxOption,
} from '@headlessui/react';
import Icon from '../ui-components/Icon';
import { twMerge } from 'tailwind-merge';

interface Option {
    occurrence?: string;
    interval?: number;
    name: string;
}
interface Props {
    repeat: Option;
    onChange: (value: Option) => void;
}

const CalendarOccurrenceDropdown = ({ repeat, onChange }: Props) => {
    const { i18next } = useAppContext();

    const options: Option[] = [
        {
            occurrence: null,
            interval: null,
            name: i18next.t('calendar.occurence.never'),
        },
        {
            occurrence: 'WEEKLY',
            interval: 1,
            name: i18next.t('every_week'),
        },
        {
            occurrence: 'WEEKLY',
            interval: 2,
            name: i18next.t('every_other_week'),
        },
        {
            occurrence: 'WEEKLY',
            interval: 3,
            name: i18next.t('every_third_week'),
        },
        {
            occurrence: 'WEEKLY',
            interval: 4,
            name: i18next.t('every_fourth_week'),
        },
    ];

    return (
        <Listbox value={repeat} by="interval" onChange={onChange}>
            <div className="relative w-full text-sm/4">
                <ListboxButton className="focus-ring w-full rounded-sm border-1 bg-gray-200 py-3 ps-3 pe-9 text-left text-gray-900">
                    {repeat.name}
                    <div className="absolute inset-y-0 end-0 flex items-center pe-2">
                        <Icon
                            className="size-5 text-gray-600"
                            iconKey="arrow_down"
                            aria-hidden="true"
                        />
                    </div>
                </ListboxButton>

                <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <ListboxOptions className="absolute -top-2 z-10 mt-1 max-h-60 w-full -translate-y-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-hidden">
                        {options.map((option) => (
                            <ListboxOption
                                key={option.interval}
                                className={({ focus }) =>
                                    twMerge(
                                        'relative cursor-default py-2 ps-3 pe-9 select-none',
                                        focus ? 'bg-gray-200' : 'text-gray-900',
                                    )
                                }
                                value={option}
                            >
                                {({ selected }) => (
                                    <span
                                        className={twMerge(
                                            'truncate text-gray-500',
                                            selected && 'font-medium',
                                        )}
                                    >
                                        {option.name}
                                    </span>
                                )}
                            </ListboxOption>
                        ))}
                    </ListboxOptions>
                </Transition>
            </div>
        </Listbox>
    );
};

export default CalendarOccurrenceDropdown;
